import { useState, useRef, useEffect } from 'react';
import { testimonials } from '@/mocks/testimonials';

const TestimonialsSection = () => {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.scroll-animate').forEach((child) => {
              child.classList.add('visible');
            });
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const prev = () => setActive((p) => (p - 1 + testimonials.length) % testimonials.length);
  const next = () => setActive((p) => (p + 1) % testimonials.length);
  const current = testimonials[active];

  return (
    <section className="py-20 md:py-28 bg-white px-4 md:px-8" ref={ref}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 scroll-animate">
          <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
            Testimonials
          </span>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-brown-dark font-bold mb-4">
            Real Stories, Real Results
          </h2>
          <div className="w-16 h-0.5 bg-brand-gold mx-auto"></div>
        </div>

        {/* Testimonial Card */}
        <div className="scroll-animate bg-brand-cream rounded-2xl p-8 md:p-12 relative">
          {/* Quote Mark */}
          <div className="w-12 h-12 flex items-center justify-center mb-6">
            <i className="ri-double-quotes-l text-5xl text-brand-gold/30 font-serif"></i>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-1 mb-5">
            {Array.from({ length: current.rating }).map((_, i) => (
              <div key={i} className="w-4 h-4 flex items-center justify-center text-brand-gold">
                <i className="ri-star-fill text-sm"></i>
              </div>
            ))}
          </div>

          {/* Review */}
          <p className="font-serif text-xl md:text-2xl text-brand-brown-dark leading-relaxed italic mb-8">
            &ldquo;{current.review}&rdquo;
          </p>

          {/* Customer Info */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-olive/20 flex-shrink-0">
                <i className="ri-user-3-line text-xl text-brand-olive"></i>
              </div>
              <div>
                <p className="font-serif text-brand-brown-dark font-semibold">
                  — {current.name}
                </p>
                <p className="font-sans text-brand-brown/60 text-sm">{current.location}</p>
              </div>
            </div>
            <span className="bg-brand-olive/10 text-brand-olive font-sans text-xs font-medium px-4 py-1.5 rounded-full">
              {current.tag}
            </span>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-brand-beige">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-beige hover:border-brand-olive hover:text-brand-olive text-brand-brown/50 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === active
                      ? 'w-6 h-2 bg-brand-olive'
                      : 'w-2 h-2 bg-brand-beige hover:bg-brand-beige-light'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-beige hover:border-brand-olive hover:text-brand-olive text-brand-brown/50 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
